import { api } from '../api';

// ── Types ─────────────────────────────────────────────────────────────

export type GranteeType = 'user' | 'project' | 'org';
export type GrantPermission = 'view' | 'edit';

export type OrgMemberForSharing = {
  user_id: string;
  email: string;
};

export type AccessGrant = {
  id: string;
  artifact_id: string;
  grantee_type: GranteeType;
  grantee_id: string;
  permission: GrantPermission;
  granted_by: string | null;
  expires_at: string | null;
  created_at: string;
};

export type ShareArtifactRequest = {
  grantee_type: GranteeType;
  grantee_id?: string;
  email?: string;
  permission?: GrantPermission;
};

export type RevokeAccessRequest = {
  grantee_type: GranteeType;
  grantee_id: string;
};

// ── API calls ─────────────────────────────────────────────────────────

/**
 * List organization members available for sharing (excludes current user).
 */
export const listOrgMembersForSharing = async (): Promise<{
  members: OrgMemberForSharing[];
}> => api.get('api/artifacts/org-members').json();

/**
 * Share an artifact with a user, project, or org.
 */
export const shareArtifact = async (
  artifactId: string,
  request: ShareArtifactRequest,
): Promise<AccessGrant> =>
  api
    .post(`api/artifacts/${artifactId}/share`, {
      json: request,
    })
    .json();

/**
 * List current access grants for an artifact.
 */
export const listArtifactShares = async (
  artifactId: string,
): Promise<{ grants: AccessGrant[] }> =>
  api.get(`api/artifacts/${artifactId}/share`).json();

/**
 * Revoke access to an artifact.
 */
export const revokeArtifactAccess = async (
  artifactId: string,
  request: RevokeAccessRequest,
): Promise<{ revoked: boolean }> =>
  api
    .delete(`api/artifacts/${artifactId}/share`, {
      json: request,
    })
    .json();
